import React, { useState, useEffect, useRef } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import { 
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  useTheme
} from '@mui/material';

// Mock data - replace with actual API calls in production
const generateMockData = (symbol, days = 30) => {
  const data = [];
  let price = symbol === 'AAPL' ? 180 : 150;
  const volatility = symbol === 'AAPL' ? 2 : 1.5;
  
  const now = new Date();
  for (let i = days; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Simulate realistic price movements
    const change = (Math.random() - 0.5) * volatility;
    price = Math.max(50, price + change);
    
    data.push({
      date: date.toISOString().split('T')[0],
      price: parseFloat(price.toFixed(2)),
      volume: Math.floor(Math.random() * 10000000) + 5000000,
    });
  }
  
  return data;
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: '0.85rem',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography color="primary" sx={{ fontWeight: 500 }}>
          ${payload[0].value.toFixed(2)}
        </Typography>
        {payload[1] && (
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Volume: {(payload[1].value / 1000000).toFixed(2)}M
          </Typography>
        )}
      </Box>
    );
  }
  
  return null;
};

// Separate chart component to avoid hooks issues
const LineChartComponent = ({ data, theme, timeRange, chartColor }) => (
  <LineChart
    data={data}
    margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
  >
    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
    <XAxis 
      dataKey="date" 
      tick={{ fontSize: 10 }}
      tickFormatter={(tick) => {
        // Format based on time range
        if (timeRange === '1D') return tick.split('-')[2]; // Day
        if (timeRange === '1W') return tick.split('-').slice(1).join('/'); // Month/Day
        return tick;
      }}
    />
    <YAxis 
      domain={['dataMin - 1', 'dataMax + 1']} 
      tick={{ fontSize: 10 }}
      width={40}
    />
    <Tooltip content={<CustomTooltip />} />
    <Line 
      type="monotone" 
      dataKey="price" 
      stroke={chartColor} 
      strokeWidth={2}
      dot={false}
      activeDot={{ r: 5 }}
    />
  </LineChart>
);

// Separate area chart component to avoid hooks issues
const AreaChartComponent = ({ data, theme, timeRange, chartColor, symbol, gradientEnd }) => (
  <AreaChart
    data={data}
    margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
  >
    <defs>
      <linearGradient id={`colorPrice${symbol}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={chartColor} stopOpacity={0.8}/>
        <stop offset="95%" stopColor={gradientEnd} stopOpacity={0}/>
      </linearGradient>
    </defs>
    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
    <XAxis 
      dataKey="date" 
      tick={{ fontSize: 10 }}
      tickFormatter={(tick) => {
        // Format based on time range
        if (timeRange === '1D') return tick.split('-')[2]; // Day
        if (timeRange === '1W') return tick.split('-').slice(1).join('/'); // Month/Day
        return tick;
      }}
    />
    <YAxis 
      domain={['dataMin - 1', 'dataMax + 1']} 
      tick={{ fontSize: 10 }}
      width={40}
    />
    <Tooltip content={<CustomTooltip />} />
    <Area 
      type="monotone" 
      dataKey="price" 
      stroke={chartColor} 
      fillOpacity={1}
      fill={`url(#colorPrice${symbol})`}
    />
  </AreaChart>
);

const StockCharts = ({ symbol }) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('1M');
  const [chartType, setChartType] = useState('line');
  const [mounted, setMounted] = useState(false);
  
  // Fetch data when component mounts or when symbol/timeRange changes
  useEffect(() => {
    setLoading(true);
    
    // Simulate API call with timeout
    const timer = setTimeout(() => {
      // Get different ranges based on selection
      const days = 
        timeRange === '1D' ? 1 : 
        timeRange === '1W' ? 7 : 
        timeRange === '1M' ? 30 : 
        timeRange === '3M' ? 90 : 
        timeRange === '1Y' ? 365 : 30;
      
      const newData = generateMockData(symbol, days);
      setData(newData);
      setLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [symbol, timeRange]);
  
  // Handle component mounting state
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };
  
  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };
  
  // Calculate price change and percentage
  const calculateChange = () => {
    if (data.length < 2) return { change: 0, percentage: 0 };
    
    const firstPrice = data[0].price;
    const lastPrice = data[data.length - 1].price;
    const change = lastPrice - firstPrice;
    const percentage = (change / firstPrice) * 100;
    
    return {
      change: change.toFixed(2),
      percentage: percentage.toFixed(2),
      positive: change >= 0
    };
  };
  
  // Make sure we have data before calculating
  const { change, percentage, positive } = data.length > 0 ? calculateChange() : { change: 0, percentage: 0, positive: true };
  
  // Add colors based on theme and price change
  const priceColor = positive ? '#22c55e' : '#ef4444';
  const chartColor = positive ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
  const gradientStart = positive ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)';
  const gradientEnd = 'rgba(255, 255, 255, 0)';
  
  // Prevent rendering issues with empty data
  if (data.length === 0 && !loading) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography>No data available for {symbol}</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%' 
        }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {/* Header with stock info */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {symbol}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {data.length > 0 && (
                  <>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      ${data[data.length - 1].price}
                    </Typography>
                    <Typography 
                      sx={{ 
                        ml: 1, 
                        color: priceColor,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {positive ? '+' : ''}{change} ({positive ? '+' : ''}{percentage}%)
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            
            {/* Chart controls */}
            <Box>
              <ToggleButtonGroup
                value={timeRange}
                exclusive
                onChange={handleTimeRangeChange}
                size="small"
                sx={{ mb: 1 }}
              >
                <ToggleButton value="1D">1D</ToggleButton>
                <ToggleButton value="1W">1W</ToggleButton>
                <ToggleButton value="1M">1M</ToggleButton>
                <ToggleButton value="3M">3M</ToggleButton>
                <ToggleButton value="1Y">1Y</ToggleButton>
              </ToggleButtonGroup>
              
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={handleChartTypeChange}
                size="small"
                sx={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <ToggleButton value="line">Line</ToggleButton>
                <ToggleButton value="area">Area</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          
          {/* Chart */}
          {mounted && data.length > 0 && (
            <Box sx={{ width: '100%', height: 200 }}>
              {/* Only render ResponsiveContainer when component is mounted and we have data */}
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChartComponent 
                    data={data} 
                    theme={theme} 
                    timeRange={timeRange} 
                    chartColor={chartColor} 
                  />
                ) : (
                  <AreaChartComponent 
                    data={data} 
                    theme={theme} 
                    timeRange={timeRange} 
                    chartColor={chartColor} 
                    symbol={symbol}
                    gradientEnd={gradientEnd}
                  />
                )}
              </ResponsiveContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StockCharts; 